import { t } from 'i18next';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { SshHandshakesTableController, State as SshTableState } from '../../../common/handshakes/controllers/SshHandshakesTableController';
import { SshHandshake } from '../../../common/handshakes/models/SshHandshake';
import { SshHandshakesRepository } from '../../../common/handshakes/services/SshHandshakesRepository';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { portNumber } from '../../../common/hooks/use-form-item/validators/network';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = SshTableState & {};

export class Controller extends SshHandshakesTableController<Props> {
  private readonly repository = new SshHandshakesRepository();

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<SshHandshake>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getSshHandshakesCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      return await this.repository.getList(ModelType.Report, report.getId(), cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'initiator_ip',
        label: t('handshakes.sourceIp'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'target_ip',
        label: t('handshakes.targetIp'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'initiator_port',
        label: t('handshakes.sourcePort'),
        type: FilterValueInputType.STRING,
        validator: () => portNumber(),
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'target_port',
        label: t('handshakes.targetPort'),
        type: FilterValueInputType.STRING,
        validator: () => portNumber(),
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'timestamp',
        label: t('handshakes.timestamp'),
        type: FilterValueInputType.TIMESTAMP,
        operations: [
          { value: Operation.LT, label: t('filterLabels.isBefore') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
        ],
      },
    ];
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the "presets" since they will trigger the rest.
      super.doInitialDataFetch();
    }
  }
}

export const ReportSshHandshakesController = withRouter<ReportOutletContextType>(Controller);
